import DOMPurify, { type Config, type UponSanitizeAttributeHookEvent } from 'dompurify';

/**
 * Sanitiser behind `SanitizedHtml`, the one component that renders job-produced HTML: node output files in
 * `NodeInstanceFileViewer` and workflow action messages in `WorkflowDetailPanel`. Anyone who can influence a job's
 * output can otherwise run script in an administrator's browser, and the session token is readable from there.
 * Only the component and the tests import it; a call site renders `SanitizedHtml` instead.
 *
 * Profile: the DOMPurify `html` profile (no SVG, no MathML — both are classic event-handler smuggling routes)
 * minus everything that executes, embeds, navigates or submits, minus data attributes, and with the URL and
 * `style` attributes filtered by the hook below. What survives is document markup: headings, paragraphs,
 * lists, tables, links, images, inline formatting, `class` and a filtered `style`.
 *
 * Decisions (see the PR for the reasoning):
 * - `style` attributes are KEPT, filtered per declaration. Job reports (`DataFrame.to_html()`, email-style
 *   messages) lean on inline styles for alignment and table layout, and `WorkflowDetailPanel` itself injects
 *   `style` to make wide tables scroll. A style attribute cannot execute script in any supported browser; the
 *   residual risks are resource loads (`url(...)`, `image-set(...)`; blocked by the CSP and dropped here) and UI redress
 *   (`position: fixed` overlays, dropped here). `<style>` ELEMENTS stay forbidden: selectors reach the whole
 *   page.
 * - `data:` URLs are dropped everywhere except `data:image/*` on `<img src>`: an inline PNG chart is how a
 *   report embeds a figure, and an image context never runs script (that includes SVG loaded through `<img>`).
 * - Runs on the client only. The app is a Vite SPA without server rendering, so `window` is always present.
 */

const FORBID_TAGS = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'link', 'meta', 'base'];
const FORBID_ATTR = ['srcdoc'];

/** Attributes whose value the browser treats as a URL. `style` is handled separately. */
const URL_ATTRIBUTES = new Set(['href', 'src', 'xlink:href', 'action', 'formaction', 'poster', 'background', 'ping', 'cite', 'longdesc']);

/**
 * Characters browsers ignore inside a URL scheme (`java\tscript:`), so they are stripped before the scheme is
 * inspected. Same set DOMPurify uses for its own URI check.
 */
// eslint-disable-next-line no-control-regex -- matching control characters is the point of this expression
const ATTR_WHITESPACE = /[\u0000-\u0020\u00a0\u1680\u180e\u2000-\u2029\u205f\u3000]/g;

/**
 * A single CSS declaration is dropped when it could load a resource, hide behind an escape sequence, or lift
 * content out of its container. `expression`, `behavior` and `-moz-binding` are dead in every supported browser
 * and cost nothing to refuse.
 */
const UNSAFE_STYLE_DECLARATION =
  /url\s*\(|image-set\s*\(|expression\s*\(|behavior\s*:|-moz-binding|@import|javascript:|\\|position\s*:\s*(fixed|sticky|absolute)\b/i;

const CONFIG: Config = {
  USE_PROFILES: { html: true },
  FORBID_TAGS,
  FORBID_ATTR,
  ALLOW_DATA_ATTR: false,
};

/** Keeps the declarations of a `style` attribute that pass `UNSAFE_STYLE_DECLARATION`; empty when none do. */
export const sanitizeStyleAttribute = (value: string): string =>
  value
    .split(';')
    .map((declaration) => declaration.trim())
    .filter((declaration) => declaration !== '' && !UNSAFE_STYLE_DECLARATION.test(declaration))
    .join('; ');

const isInlineImageSource = (node: Element, attrName: string, url: string): boolean =>
  attrName === 'src' && node.nodeName.toLowerCase() === 'img' && url.startsWith('data:image/');

const filterAttribute = (node: Element, event: UponSanitizeAttributeHookEvent): void => {
  const attrName = event.attrName.toLowerCase();

  if (attrName === 'style') {
    event.attrValue = sanitizeStyleAttribute(event.attrValue);
    if (event.attrValue === '') event.keepAttr = false;
    return;
  }

  if (!URL_ATTRIBUTES.has(attrName)) return;

  const url = event.attrValue.replace(ATTR_WHITESPACE, '').toLowerCase();
  if (url.startsWith('javascript:') || url.startsWith('vbscript:')) {
    event.keepAttr = false;
    return;
  }
  if (url.startsWith('data:') && !isInlineImageSource(node, attrName, url)) {
    event.keepAttr = false;
  }
};

// A dedicated instance so the hook cannot leak into any other DOMPurify consumer in the bundle.
const purifier = DOMPurify(window);
purifier.addHook('uponSanitizeAttribute', filterAttribute);

/**
 * Returns `html` reduced to inert document markup. Safe to hand to `dangerouslySetInnerHTML`; nothing in the
 * result can execute, embed a document, submit a form or change the page's base URL.
 */
export const sanitizeHtml = (html: string): string => purifier.sanitize(html, CONFIG);
